import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ClientAuthShell } from "./ClientShell";
import { capi, apiError } from "./clientApi";

const LINK_DEAD =
  "Ce lien n'est plus valide. Il a peut-être déjà été utilisé ou a expiré. Contactez SLIZ pour en recevoir un nouveau.";

// Sert à la fois pour l'acceptation d'invitation et la réinitialisation.
export default function ClientSetPassword({ mode }) {
  const navigate = useNavigate();
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("checking"); // checking | valid | dead

  const isInvite = mode === "invite";
  const endpoint = isInvite ? "/auth/accept-invitation" : "/auth/reset-password";
  const title = isInvite ? "Activez votre compte" : "Nouveau mot de passe";

  // Vérification du lien À L'OUVERTURE (lecture seule, ne consomme pas le token).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await capi(`/auth/token-status?token=${encodeURIComponent(token || "")}`);
        const d = await res.json().catch(() => ({}));
        if (!alive) return;
        setStatus(res.ok && d.valid ? "valid" : "dead");
      } catch {
        if (alive) setStatus("dead");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 12) {
      setErr("Le mot de passe doit contenir au moins 12 caractères.");
      return;
    }
    if (password !== confirm) {
      setErr("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      const res = await capi(endpoint, { method: "POST", body: { token, password } });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const detail = typeof d.detail === "string" ? d.detail : "";
        if (res.status === 400 && detail.startsWith("Lien invalide")) {
          setStatus("dead"); // lien invalide / expiré / déjà utilisé -> message clair, pas de formulaire
        } else if (res.status >= 500) {
          setErr("Une erreur est survenue.");
        } else {
          setErr(apiError(d.detail));
        }
        setBusy(false);
        return;
      }
      setOk(true);
      setTimeout(() => navigate("/espace-client/connexion"), 2200);
    } catch {
      setErr("Une erreur est survenue.");
      setBusy(false);
    }
  };

  return (
    <ClientAuthShell>
      <div className="cp-card" data-testid="cp-setpw-card">
        <h1 className="cp-title">{title}</h1>

        {status === "checking" && (
          <p className="cp-sub" data-testid="cp-setpw-checking">Vérification du lien…</p>
        )}

        {status === "dead" && (
          <>
            <p className="cp-error" data-testid="cp-setpw-dead">{LINK_DEAD}</p>
            <button className="cp-btn cp-btn-primary" data-testid="cp-setpw-goto-login"
              onClick={() => navigate("/espace-client/connexion")}>
              Aller à la connexion
            </button>
          </>
        )}

        {status === "valid" && (ok ? (
          <p className="cp-ok" data-testid="cp-setpw-ok">
            C'est fait. Redirection vers la connexion…
          </p>
        ) : (
          <form onSubmit={submit} data-testid="cp-setpw-form">
            <p className="cp-sub">Choisissez un mot de passe d'au moins 12 caractères.</p>
            {err && <p className="cp-error" data-testid="cp-setpw-error">{err}</p>}
            <div className="cp-field">
              <label htmlFor="cp-np">Mot de passe</label>
              <input id="cp-np" className="cp-input" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="cp-setpw-password" autoComplete="new-password" />
            </div>
            <div className="cp-field">
              <label htmlFor="cp-cp">Confirmez le mot de passe</label>
              <input id="cp-cp" className="cp-input" type="password" required
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="cp-setpw-confirm" autoComplete="new-password" />
            </div>
            <button className="cp-btn cp-btn-primary" type="submit" disabled={busy}
              data-testid="cp-setpw-submit">
              {busy ? "Enregistrement…" : "Valider"}
            </button>
          </form>
        ))}
      </div>
    </ClientAuthShell>
  );
}
